import React, { useMemo, useState } from 'react';
import { sendSlackNotification } from '../utils/slackNotifications';
import useSeo from '../hooks/useSeo';
import '../styles/pages/Legal.css';
import '../styles/pages/ContactUs.css';

function ContactUs() {
  const seoConfig = useMemo(() => {
    const title = 'Contact | BloomNav Technology Studio';
    const description =
      'Tell BloomNav about your project. We reply within one business day with next steps, a call time, or a rough estimate.';
    return {
      title,
      description,
      canonicalPath: '/contact',
      ogType: 'website',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav'
    };
  }, []);

  useSeo(seoConfig);

  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    subject: 'New project',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await sendSlackNotification(formData, 'contact');
    setIsSubmitted(true);
  };

  return (
    <div className="legal-page cu-page">
      <div className="legal-head">
        <span className="legal-kicker">Contact</span>
        <h1 className="legal-title">
          Tell us what
          <br />
          <em>you want to build.</em>
        </h1>
        <p className="legal-sub">
          A short note is enough. We will reply within one business day with next steps, a call
          time, or a rough sense of fit and cost.
        </p>
      </div>

      <div className="cu-layout">
        <aside className="cu-aside">
          <h2>Other ways to reach us</h2>
          <p>
            Prefer email or a call? Use the details below. For project enquiries the form is usually
            the fastest way to get a useful reply.
          </p>

          <ul className="cu-methods">
            <li className="cu-method">
              <span className="cu-method-label">Email</span>
              <a href="mailto:support@bloomnav.com">support@bloomnav.com</a>
              <p>We read every message.</p>
            </li>
            <li className="cu-method">
              <span className="cu-method-label">Phone</span>
              <a href="tel:+918007385851">+91 80073 85851</a>
              <p>Monday-Friday, 9am-5pm IST</p>
            </li>
            <li className="cu-method">
              <span className="cu-method-label">Studio</span>
              <span>CLIVEXCI SYSTEMS LLP</span>
              <p>Saligao, Goa, India</p>
            </li>
          </ul>

          <div className="cu-note">
            <p>
              <strong>Already a client?</strong> Use your portal login for project updates, or email
              us from the address on your account so we can find your records quickly.
            </p>
          </div>
        </aside>

        {isSubmitted ? (
          <div className="cu-success">
            <div className="cu-success-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="m5 13 4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2>Message received</h2>
            <p>
              Thanks. We have your note and will reply within one business day, usually sooner.
            </p>
            <p>
              If it is urgent, call us at <strong>+91 80073 85851</strong> during working hours.
            </p>
          </div>
        ) : (
          <div className="cu-card">
            <div className="cu-card-head">
              <h2>Send a message</h2>
              <p>Share a little about the project. No need for a full brief yet.</p>
            </div>

            <form className="cu-form" onSubmit={handleSubmit}>
              <div className="cu-row">
                <div className="cu-field">
                  <label htmlFor="name">Your name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div className="cu-field">
                  <label htmlFor="companyName">Company</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Your company"
                  />
                </div>
              </div>

              <div className="cu-row">
                <div className="cu-field">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div className="cu-field">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              <div className="cu-field">
                <label htmlFor="subject">What is this about?</label>
                <select id="subject" name="subject" value={formData.subject} onChange={handleChange}>
                  <option value="New project">New project</option>
                  <option value="Ongoing work">Ongoing work / retainer</option>
                  <option value="Pricing">Pricing question</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Support">Existing project support</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="cu-field">
                <label htmlFor="message">Your message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="What are you building, who is it for, and when do you want to start?"
                  rows="5"
                  required
                ></textarea>
              </div>

              <button type="submit" className="cu-submit">
                Send message
              </button>

              <p className="cu-form-foot">We reply within one business day.</p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactUs;
